import type { RefOrGetter } from '@/helpers'
import {
    type App,
    computed,
    inject,
    nextTick,
    onMounted,
    ref,
    type Ref,
    watch,
} from 'vue'
import type { RegionCode } from '@/graphql/api'
import type { AffirmConfig } from '@/entry-server-types.ts'
import { getDocument, getWindow } from '@/helpers/globals.ts'

const revisionKey = '__plugin_affirm_revision_key'
const pluginKey = '__plugin_affirm'

export interface AffirmPlugin {
    load(): Promise<Affirm | undefined>

    refresh(): Promise<void>
}

export function installAffirm(
    app: App,
    regionCode: Ref<Readonly<RegionCode>>,
    configUS: AffirmConfig | undefined,
    configGB: AffirmConfig | undefined
): void {
    let affirm: Affirm | undefined = undefined
    let lastAffirm: AffirmConfig | undefined

    const config = computed((): AffirmConfig | undefined => {
        switch (regionCode.value) {
            case 'US':
                return configUS
            case 'UK':
                return configGB
            default:
                return undefined
        }
    })

    watch(config, () => {
        if (affirm) {
            load()
        }
    })

    async function load(): Promise<Affirm | undefined> {
        if (!config.value) {
            return undefined
        }

        if (affirm && lastAffirm === config.value) {
            return affirm
        }

        lastAffirm = config.value

        affirm = await injectAffirm(
            config.value.publicKey,
            config.value.scriptUrl
        )

        return affirm
    }

    async function refresh(): Promise<void> {
        const affirm = await load()

        affirm?.ui?.refresh?.()
    }

    const plugin: AffirmPlugin = {
        load,
        refresh,
    }

    app.provide(pluginKey, plugin)

    const revision = ref(0)
    app.provide(revisionKey, revision)

    watch(revision, async () => {
        await nextTick()
        await plugin.refresh()
    })
}

function useAffirmRevision(): Ref<number> {
    return inject(revisionKey) as Ref<number>
}

export function useAffirm(): AffirmPlugin {
    return inject(pluginKey) as AffirmPlugin
}

export function useAffirmRefresh(
    refreshKey: RefOrGetter<string | number | null>
): void {
    const { refresh } = useAffirm()

    onMounted(refresh)

    const revision = useAffirmRevision()

    watch(refreshKey, () => {
        revision.value = revision.value + 1
    })
}

async function injectAffirm(
    publicApiKey: string,
    scriptUrl: string
): Promise<Affirm | undefined> {
    const window = getWindow()

    if (!window) {
        return undefined
    }

    const document = getDocument()

    if (!document) {
        return undefined
    }

    return new Promise((resolve) => {
        const scriptElement = document.createElement('script')
        scriptElement.async = true
        scriptElement.src = scriptUrl

        const firstScript = document.getElementsByTagName('script')[0]
        firstScript?.parentNode?.insertBefore(scriptElement, firstScript)

        scriptElement.onload = () => {
            // The original Affirm init code calls f(g)
            // Which effectively calls window.affirm.checkout({public_api_key: "someKey"})
            // window.affirm.checkout({public_api_key: publicApiKey})
            window?.affirm?.apiKey.setPublicApiKey(publicApiKey)
            window?.affirm?.jsReady(() => {
                resolve(window.affirm)
            })
        }
    })
}

type AffirmUiComponentsCreateOptions = {
    pageType: 'product'
    amount?: number
    affirmLogoType?: 'logo' | 'symbol' | 'text'
    affirmLogoColor?: 'black' | 'blue' | 'white'
    category?: string
    brand?: string
    sku?: string
    promoId?: string
    learnMoreShow?: boolean
}

export type Affirm = {
    apiKey: {
        getPublicApiKey(): string
        setPublicApiKey(key: string): void
    }
    jsReady: (cb: () => void) => void
    checkout?: {
        (data: Record<string, any> | null): void
        open: () => void
    }
    ui: {
        ready: (cb: () => void) => void
        refresh?: () => void
        components: {
            create: (
                type: 'promo',
                options: AffirmUiComponentsCreateOptions
            ) => {
                render: (selector: string) => void
                update: (
                    options: Partial<AffirmUiComponentsCreateOptions>
                ) => void
            }
        }
    }
}
